import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { BsCloudUpload } from "react-icons/bs";
import PopupModal from "../../PopupModal";
import { PrimaryButton, SecondaryButton } from "../../../StyledCss/styledbtn";
import {
  getResumeImportStatus,
  parseResumes,
  saveResumes,
  storeResumeImportCandidates,
} from "../../../api/services/hroneApi";
import {
  IMPORT_STATUS,
  buildCreateCandidatePayload,
  normalizeApplication,
  normalizeImportResults,
  validateZipContainsOnlyPdfs,
} from "../../../util/hiringUtils";
import "./index.css";

const STEP = { SELECT: "select", PROCESSING: "processing" };

const ACCEPT        = ".pdf,.zip";
const POLL_INTERVAL = 2500;

const ImportResumesModal = ({ jobId, isOpen, onClose, onImportComplete }) => {
  const orgDetails   = JSON.parse(localStorage.getItem("org"));
  const primaryColor = orgDetails?.primaryColor || "#e86020";

  const inputRef = useRef(null);
  const abortRef = useRef(null);

  const [step,          setStep]          = useState(STEP.SELECT);
  const [isDragging,    setIsDragging]    = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading,   setIsUploading]   = useState(false);
  const [simProgress,   setSimProgress]   = useState(0);
  const [progressTotal, setProgressTotal] = useState(0);

  const reset = () => {
    abortRef.current?.abort();
    setStep(STEP.SELECT);
    setSelectedFiles([]);
    setIsDragging(false);
    setIsUploading(false);
    setSimProgress(0);
    setProgressTotal(0);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleClose = () => {
    if (isUploading) return;
    reset();
    onClose();
  };

  const validateFiles = (fileList) => {
    const files = Array.from(fileList || []);
    const valid = files.filter((f) => {
      const ext = f.name.split(".").pop()?.toLowerCase();
      return ["pdf", "zip"].includes(ext);
    });
    if (valid.length !== files.length) {
      toast.error("Some files were skipped. Only PDF and ZIP are supported.");
    }
    return valid;
  };

  const handleFiles = async (fileList) => {
    const valid = validateFiles(fileList);
    if (!valid.length) return;

    const zipChecks = await Promise.all(
      valid.map((file) => {
        const ext = file.name.split(".").pop()?.toLowerCase();
        return ext === "zip" ? validateZipContainsOnlyPdfs(file) : true;
      })
    );

    const accepted = valid.filter((_, i) => zipChecks[i]);
    const rejectedNames = valid.filter((_, i) => !zipChecks[i]).map((f) => f.name);
    if (rejectedNames.length) {
      toast.error(
        `${rejectedNames.join(", ")} skipped — ZIP files must contain only PDF resumes.`
      );
    }

    if (accepted.length) setSelectedFiles(accepted);
  };

  const autoSaveReady = async (readyCandidates, sessionId) => {
    if (!readyCandidates.length) return [];

    const payload = readyCandidates.map((c) =>
      buildCreateCandidatePayload(
        {
          name:              c.name,
          email:             c.email,
          phone:             c.phone,
          skills:            c.skills            || "",
          experience:        c.experience        || "",
          education:         c.education         || "",
          previousCompanies: c.previousCompanies || "",
          certifications:    c.certifications    || "",
          projects:          c.projects          || "",
          designation:       c.designation       || "",
        },
        { parseId: c.parseId, tempId: c.tempId }
      )
    );

    const res  = await saveResumes(jobId, { session_id: sessionId, candidates: payload });
    const data = await res.json();
    if (!data.status) throw new Error(data.message || data.detail || "Auto-save failed");

    const rawList = data.data?.applications || data.data?.candidates || data.data || [];
    return (Array.isArray(rawList) ? rawList : []).map(normalizeApplication).filter(Boolean);
  };

  const persistPending = async (sessionId, pending, failedFiles) => {
    try {
      await storeResumeImportCandidates(jobId, {
        session_id: sessionId,
        candidates: pending.map((c) => ({
          temp_id:        c.tempId,
          parse_id:       c.parseId,
          import_status:  c.importStatus,
          file_name:      c.resumeName,
          name:           c.name,
          email:          c.email,
          phone:          c.phone,
          skills:         c.skills,
          experience:     c.experience,
          education:      c.education,
          previous_companies: c.previousCompanies,
          certifications: c.certifications,
          projects:       c.projects,
          designation:    c.designation,
          ats_score:      c.atsScore,
          recommendation: c.aiRecommendation,
          resume_url:     c.resumeUrl,
        })),
        failed_files: failedFiles.map((f) => ({
          file_name: f.fileName,
          reason:    f.reason,
        })),
      });
    } catch {
      // Backend may not have this endpoint yet — local fallback via onImportComplete
    }
  };

  const finishImport = async (completedData, sessionId) => {
    const { validCandidates, failedFiles } = normalizeImportResults(completedData);

    const ready        = validCandidates.filter((c) => c.importStatus === IMPORT_STATUS.READY);
    const pending      = validCandidates.filter((c) => c.importStatus !== IMPORT_STATUS.READY);
    const needsReview  = pending.filter((c) => c.importStatus === IMPORT_STATUS.NEEDS_REVIEW);
    const duplicate    = pending.filter((c) => c.importStatus === IMPORT_STATUS.DUPLICATE);

    await persistPending(sessionId, pending, failedFiles);

    let savedApplications = [];
    if (ready.length) {
      try {
        savedApplications = await autoSaveReady(ready, sessionId);
        toast.success(
          `${ready.length} ready candidate${ready.length > 1 ? "s" : ""} saved automatically`
        );
      } catch (err) {
        toast.error(err.message || "Failed to auto-save ready candidates");
      }
    }

    const counts = {
      READY:        ready.length,
      NEEDS_REVIEW: needsReview.length,
      DUPLICATE:    duplicate.length,
      FAILED:       failedFiles.length,
    };

    onImportComplete?.({
      sessionId,
      savedApplications,
      pendingCandidates: pending,
      failedFiles,
      counts,
      savedTempIds: ready.map((c) => c.tempId).filter(Boolean),
    });

    reset();
    onClose();

    if (needsReview.length) {
      toast(`${needsReview.length} candidate${needsReview.length > 1 ? "s" : ""} need review`, { icon: "ℹ️" });
    }
  };

  const pollImportUntilComplete = async (sid, signal) => {
    while (true) {
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

      const res  = await getResumeImportStatus(jobId, sid, signal);
      const data = await res.json();
      if (!data.status) throw new Error(data.message || data.detail || "Failed to check import status");

      const statusData = data.data || {};
      const processed  = statusData.processed ?? statusData.processed_count ?? statusData.completed_count;
      if (processed != null) setSimProgress(Number(processed));

      const status = String(statusData.status || "").toUpperCase();
      if (status === "COMPLETED") return statusData;
      if (status === "FAILED" || status === "ERROR") {
        throw new Error(statusData.message || data.message || "Import failed");
      }
    }
  };

  const handleImport = async () => {
    if (!selectedFiles.length || isUploading) return;

    const total = selectedFiles.length;
    setProgressTotal(total);
    setSimProgress(0);
    setStep(STEP.PROCESSING);
    setIsUploading(true);

    const fd = new FormData();
    selectedFiles.forEach((file) => {
      fd.append("files", file, file.name);
    });

    abortRef.current = new AbortController();

    try {
      const res  = await parseResumes(jobId, fd, abortRef.current.signal);
      const data = await res.json();
      if (!data.status) throw new Error(data.message || data.detail || "Import failed");

      const startData  = data.data || {};
      const sid        = startData.session_id;
      const totalFiles = startData.total_files ?? total;

      if (!sid) throw new Error("Import session was not created");

      setProgressTotal(totalFiles);

      const completedData = await pollImportUntilComplete(sid, abortRef.current.signal);
      setSimProgress(totalFiles);

      await finishImport({ ...completedData, session_id: sid }, sid);
    } catch (err) {
      if (err.name === "AbortError") {
        setStep(STEP.SELECT);
        return;
      }
      if (!navigator.onLine) {
        toast.error("Network error. Please check your connection.");
      } else {
        toast.error(err.message || "Import failed. Please try again.");
      }
      setStep(STEP.SELECT);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancelProcessing = () => {
    abortRef.current?.abort();
    setIsUploading(false);
    setStep(STEP.SELECT);
  };

  if (!isOpen) return null;

  const progressPct    = progressTotal > 0 ? Math.round((simProgress / progressTotal) * 100) : 0;
  const displayCurrent = Math.min(simProgress, progressTotal);

  if (step === STEP.PROCESSING) {
    return (
      <PopupModal
        showModal={isOpen}
        modalName="Uploading Resumes"
        closeBtn={() => {}}
        width="520px"
      >
        <div className="ir-modal ir-processing" style={{ "--ir-primary": primaryColor }}>
          <p className="ir-processing-title">Uploading resumes…</p>

          <div className="ir-progress-track">
            <div className="ir-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>

          <p className="ir-processing-sub">
            Processing resume {displayCurrent} of {progressTotal}
          </p>
          <p className="ir-processing-note">
            AI is parsing your resumes. Ready candidates will be saved automatically.
          </p>

          <button type="button" className="ir-cancel-link" onClick={handleCancelProcessing}>
            Cancel
          </button>
        </div>
      </PopupModal>
    );
  }

  return (
    <PopupModal
      showModal={isOpen}
      modalName="Upload Resume"
      closeBtn={handleClose}
      width="520px"
    >
      <div className="ir-modal" style={{ "--ir-primary": primaryColor }}>
        <p className="ir-hint">
          Upload a single resume, multiple resumes, or a ZIP archive. Ready candidates are saved
          automatically; others appear in Needs Review, Duplicate, or Failed tabs.
        </p>

        <div
          className={`ir-dropzone${isDragging ? " ir-dropzone--active" : ""}`}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => { e.preventDefault(); setIsDragging(false); handleFiles(e.dataTransfer.files); }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            style={{ display: "none" }}
            onChange={(e) => handleFiles(e.target.files)}
          />
          <BsCloudUpload className="ir-dropzone-icon" />
          <p className="ir-dropzone-title">
            {selectedFiles.length
              ? `${selectedFiles.length} file${selectedFiles.length > 1 ? "s" : ""} selected`
              : "Drag & drop resumes here"}
          </p>
          <p className="ir-dropzone-sub">
            {selectedFiles.length
              ? "Click to change selection"
              : "or click to browse — PDF or ZIP"}
          </p>
        </div>

        {selectedFiles.length > 0 && (
          <ul className="ir-file-list">
            {selectedFiles.map((f) => (
              <li key={`${f.name}-${f.size}`}>{f.name}</li>
            ))}
          </ul>
        )}

        <div className="ir-actions">
          <SecondaryButton type="button" onClick={handleClose}>Cancel</SecondaryButton>
          <PrimaryButton
            type="button"
            disabled={!selectedFiles.length}
            onClick={handleImport}
          >
            Upload Resume
          </PrimaryButton>
        </div>
      </div>
    </PopupModal>
  );
};

export default ImportResumesModal;
